import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ShopSales } from '@popcart/core/v1';
import { api } from '../../api';
import { Loading, Segmented, StatusPill, useTitle } from '../../ui/bits';
import { Fig, Stat } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { Scene, SceneError } from '../../catalog/art';
import { count, peso } from '../../lib/format.js';
import { useShop } from '../ShopLayout';
import {
  PAYOUT_VIEW,
  PRESETS,
  dayBars,
  isEmptyPeriod,
  payoutNote,
  periodLabel,
  presetRange,
  rangeProblem,
  salesActions,
  statementCards,
  topBars,
  type Preset,
} from './lib/sales.js';
import { salesKeys } from './keys';
import './sales.css';
import { DateField } from '../../ui/controls';

function isPreset(v: string | null): v is Preset {
  return v === 'thisWeek' || v === 'lastWeek' || v === 'thisMonth' || v === 'custom';
}

/** /shop/:shopId/sales — the period's statement (the same one Ops pays from), daily bars, top products and payouts. */
export function ShopSalesPage() {
  useTitle('Sales & payouts');
  const { shopId } = useShop();
  const [params, setParams] = useSearchParams();
  const [now] = useState(() => Date.now());
  const preset: Preset = isPreset(params.get('period')) ? (params.get('period') as Preset) : 'thisWeek';
  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const customOk = preset === 'custom' && !rangeProblem(customFrom, customTo, now);
  const range = preset === 'custom' ? (customOk ? { from: customFrom, to: customTo } : null) : presetRange(preset, now);
  const actions = useMemo(() => salesActions(api, shopId), [shopId]);

  const sales = useQuery({
    queryKey: salesKeys.sales(shopId, range?.from ?? '', range?.to ?? ''),
    queryFn: () => actions.load(range as { from: string; to: string }),
    enabled: !!shopId && !!range,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const setPeriod = (p: Preset) => {
    const next = new URLSearchParams(params);
    if (p === 'thisWeek') next.delete('period');
    else next.set('period', p);
    if (p === 'custom' && !customFrom) {
      const r = presetRange('thisWeek', now);
      next.set('from', r.from);
      next.set('to', r.to);
    }
    if (p !== 'custom') {
      next.delete('from');
      next.delete('to');
    }
    setParams(next, { replace: true });
  };

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Sales &amp; payouts</h1>
          <span className="dv-live" title="Delivered orders only">
            {range ? `${periodLabel(range.from, range.to, now)} · delivered` : 'Pick a period'}
          </span>
        </div>
        <div className="dv-actions">
          <Segmented<Preset> label="Period" value={preset} options={PRESETS} onChange={setPeriod} />
        </div>
      </header>

      {preset === 'custom' ? <CustomRange key={`${customFrom}:${customTo}`} from={customFrom} to={customTo} now={now} params={params} setParams={setParams} /> : null}

      {sales.isError && !sales.data ? (
        <section className="card">
          <SceneError error={sales.error} onRetry={() => void sales.refetch()} />
        </section>
      ) : !range ? null : !sales.data ? (
        <Loading label="Loading sales" rows={3} />
      ) : (
        <SalesBody s={sales.data} busy={sales.isFetching} />
      )}
    </>
  );
}

function CustomRange({ from, to, now, params, setParams }: { from: string; to: string; now: number; params: URLSearchParams; setParams: (p: URLSearchParams, o?: { replace?: boolean }) => void }) {
  const [f, setF] = useState(from);
  const [t, setT] = useState(to);
  const [err, setErr] = useState<string | null>(null);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const p = rangeProblem(f, t, now);
    setErr(p);
    if (p) return;
    const next = new URLSearchParams(params);
    next.set('period', 'custom');
    next.set('from', f);
    next.set('to', t);
    setParams(next, { replace: true });
  };
  return (
    <form className="card dv-card sl-custom dv-rise" onSubmit={onSubmit} noValidate aria-label="Custom period">
      <div className="row wrap sl-custom-row">
        <div className="field">
          <label htmlFor="sl-from">From</label>
          <DateField id="sl-from" className="input" value={f} aria-invalid={err ? true : undefined} onChange={(e) => setF(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="sl-to">To</label>
          <DateField id="sl-to" className="input" value={t} aria-invalid={err ? true : undefined} onChange={(e) => setT(e.target.value)} />
        </div>
        <button type="submit" className="btn btn-quiet">
          Show
        </button>
      </div>
      {err ? (
        <span className="pill st-error err-pill" role="alert">
          {err}
        </span>
      ) : null}
    </form>
  );
}

function SalesBody({ s, busy }: { s: ShopSales; busy: boolean }) {
  const cards = statementCards(s.statement);
  const bars = dayBars(s.days);
  const top = topBars(s.topProducts);
  const dense = bars.length > 14;
  const empty = isEmptyPeriod(s);
  return (
    <div className="stack s24" aria-busy={busy || undefined}>
      <div className="dv-stats sl-kpis dv-rise" role="group" aria-label="Statement">
        {cards.map((c) => (
          <div key={c.key} className={c.key === 'net' ? 'sl-net' : undefined}>
            <Stat label={c.label} value={c.value} sub={c.detail} tone={c.key === 'net' ? 'ink' : undefined} />
          </div>
        ))}
      </div>

      <div className="sl-grid">
        <section className="card dv-card dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="sl-days-h">
          <div className="dv-card-h">
            <h2 id="sl-days-h">Day by day</h2>
            <span className="grow" />
            <span className="dv-sub">Gross · delivered</span>
          </div>
          {empty ? (
            <Scene scene="noOrders" compact title="No delivered orders in this period">
              Sales show up here once orders reach customers.
            </Scene>
          ) : (
            <div className="sl-days-wrap">
              <ol className={`sl-days${dense ? ' dense' : ''}`} aria-label="Gross sales by day">
                {bars.map((b, i) => (
                  <li key={b.day} className="sl-day" title={b.aria}>
                    <span className="sl-col" role="img" aria-label={b.aria} style={{ ['--j' as string]: i } as CSSProperties}>
                      <i className={b.pct ? '' : 'empty'} style={{ height: `${b.pct}%` } as CSSProperties} />
                    </span>
                    <span className="sl-lbl" aria-hidden="true">
                      {!dense || i % 7 === 0 ? b.label : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>

        <section className="card dv-card dv-rise" style={{ ['--i' as string]: 2 }} aria-labelledby="sl-top-h">
          <div className="dv-card-h">
            <h2 id="sl-top-h">Top products</h2>
            {top.length ? <span className="dv-chip">{top.length}</span> : null}
          </div>
          {top.length ? (
            <ol className="sl-top">
              {top.map((p, i) => (
                <li key={p.productId} className="sl-toprow">
                  <span className="sl-rank" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="sl-topmain">
                    <span className="row between">
                      <b className="sl-topnm">{p.name}</b>
                      <Fig text={p.value} size="sm" />
                    </span>
                    <span className="dv-share" aria-hidden="true">
                      <i className={p.pct ? '' : 'empty'} style={{ width: `${p.pct}%` } as CSSProperties} />
                    </span>
                    <span className="dv-sub">{count(p.qty)} sold</span>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="dv-quiet">Nothing sold in this period yet</p>
          )}
        </section>
      </div>

      <section className="card dv-card dv-rise" style={{ ['--i' as string]: 3 }} aria-labelledby="sl-pay-h">
        <div className="dv-card-h">
          <h2 id="sl-pay-h">Payouts</h2>
          {s.payouts.length ? <span className="dv-chip">{s.payouts.length}</span> : null}
          <span className="grow" />
          <span className="dv-sub">
            <Icon name="calendar" size="sm" />
            Weekly · Mon–Sun
          </span>
        </div>
        {s.payouts.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Period</th>
                  <th scope="col" className="num">
                    Orders
                  </th>
                  <th scope="col" className="num">
                    Gross
                  </th>
                  <th scope="col" className="num">
                    Net
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">Reference</th>
                </tr>
              </thead>
              <tbody>
                {s.payouts.map((p) => {
                  const v = PAYOUT_VIEW[p.status];
                  return (
                    <tr key={p.id}>
                      <td>{periodLabel(p.from, p.to)}</td>
                      <td className="num">{count(p.ordersCount)}</td>
                      <td className="num">{peso(p.grossCentavos)}</td>
                      <td className="num">
                        <Fig text={peso(p.netCentavos)} size="xs" animate={false} />
                      </td>
                      <td>
                        <StatusPill tone={v.tone}>{v.label}</StatusPill>
                      </td>
                      <td>
                        <span className="stack tight">
                          {p.reference ? <span className="t-mono sl-ref">{p.reference}</span> : null}
                          <span className="dv-sub">{payoutNote(p)}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="dv-quiet" title="PopCart creates one after each week closes, then pays it and adds the reference here.">
            <Icon name="calendar" />
            No payouts yet · one lands after each week closes
          </p>
        )}
      </section>
    </div>
  );
}
